/**
 * SnapshotStatsTracker — the inter-client smoothness verdict instrument
 * (2026-05-29).
 *
 * The SnapshotInterpolator is the shared smoothing primitive for remote
 * entities. To tune it data-first (project rule: "measure before opening an
 * arc") we need to see, in the field:
 *   - inter-arrival cadence of snapshots PER ENTITY TYPE — mobs change schema
 *     every server tick (~60Hz); remote players only when a ~20Hz position
 *     claim lands. The histogram reveals each mode + its jitter spread.
 *   - how often a render frame falls into the extrapolation branch (renderTime
 *     past the latest snapshot) or the hitch/freeze branch (packet loss). High
 *     extrapolation% on players ⇒ render-lag too shallow for the 20Hz source.
 *   - live interpolator buffer depth.
 *
 * Design (mirrors FrameJitterTracker): singleton, default-OFF, zero hot-path
 * cost when disabled (early-return), `?snapstats=1` URL opt-in or rig enable.
 * Counters live in 1-second buckets so record* is O(1) and memory is bounded
 * regardless of entity count (no per-sample ring — players + mobs sample at
 * 60Hz × N entities).
 *
 * Producers: the two managers call recordPush() at their interp.push() site
 * and recordSample() after interp.sample(). Consumers: an in-game panel +
 * the server aggregate (SnapshotStatsReporter → netobs:snapshotStats →
 * /api/diag/snapshot-stats). No new transport — reuses the netobs spine.
 */

import { browserOnly } from '../../shared/util/browserOnly';
import type {
  SnapshotEntityType,
  SnapshotStatsSnapshotWire,
  SnapshotStatsPerTypeWire,
  SnapshotGapBucketWire,
} from '../../shared/diagnostics/SnapshotStatsTypes';
import { bootSearchParams } from '../../config/boot/bootUrl';

const STORAGE_FLAG_NAME = 'snapstats';
const WINDOW_SEC = 5;                 // rolling stats window
const MAX_GAP_MS = 2_000;             // gaps above this = re-appearance/teleport, not cadence
const LAST_PUSH_STALE_MS = 60_000;    // prune per-entity lastPush entries older than this

// Gap buckets chosen so the two nominal cadences land cleanly: 60Hz (~16.7ms)
// in '<20ms', 20Hz (50ms) in '40-60ms'. The 60ms+ tail is where jitter starts
// to threaten the ~90-100ms render-lag (→ extrapolation).
const GAP_BUCKETS: Array<{ label: string; lo: number; hi: number }> = [
  { label: '<20ms',   lo: 0,   hi: 20 },
  { label: '20-40',   lo: 20,  hi: 40 },
  { label: '40-60',   lo: 40,  hi: 60 },
  { label: '60-90',   lo: 60,  hi: 90 },
  { label: '90-130',  lo: 90,  hi: 130 },
  { label: '>130ms',  lo: 130, hi: Infinity },
];

interface SecondBucket {
  sec: number;
  pushCount: number;
  gapSum: number;
  gapBuckets: number[];      // parallel to GAP_BUCKETS
  sampleCount: number;
  extrapolated: number;
  hitched: number;
  depthSum: number;
}

function makeBucket(sec: number): SecondBucket {
  return {
    sec,
    pushCount: 0,
    gapSum: 0,
    gapBuckets: GAP_BUCKETS.map(() => 0),
    sampleCount: 0,
    extrapolated: 0,
    hitched: 0,
    depthSum: 0,
  };
}

function gapBucketIndex(gapMs: number): number {
  for (let i = 0; i < GAP_BUCKETS.length; i++) {
    if (gapMs >= GAP_BUCKETS[i].lo && gapMs < GAP_BUCKETS[i].hi) return i;
  }
  return GAP_BUCKETS.length - 1;
}

class TypeChannel {
  buckets: SecondBucket[] = [];
  lastPushAt = new Map<string, number>();

  private currentBucket(nowMs: number): SecondBucket {
    const sec = Math.floor(nowMs / 1000);
    let head = this.buckets[this.buckets.length - 1];
    if (!head || head.sec !== sec) {
      head = makeBucket(sec);
      this.buckets.push(head);
    }
    // Prune buckets older than the window.
    const cutoff = sec - WINDOW_SEC;
    while (this.buckets.length > 0 && this.buckets[0].sec < cutoff) {
      this.buckets.shift();
    }
    return head;
  }

  recordPush(entityId: string, nowMs: number): void {
    const bucket = this.currentBucket(nowMs);
    const last = this.lastPushAt.get(entityId);
    this.lastPushAt.set(entityId, nowMs);
    if (last !== undefined) {
      const gap = nowMs - last;
      // Skip implausible gaps (entity was absent / teleported) — not a cadence
      // sample. Still updated lastPushAt above so the next gap is clean.
      if (gap > 0 && gap <= MAX_GAP_MS) {
        bucket.pushCount++;
        bucket.gapSum += gap;
        bucket.gapBuckets[gapBucketIndex(gap)]++;
      }
    }
    // Lazy prune of stale per-entity entries.
    if (this.lastPushAt.size > 64) {
      for (const [id, at] of this.lastPushAt) {
        if (nowMs - at > LAST_PUSH_STALE_MS) this.lastPushAt.delete(id);
      }
    }
  }

  recordSample(nowMs: number, isExtrapolated: boolean, isHitched: boolean, bufferDepth: number): void {
    const bucket = this.currentBucket(nowMs);
    bucket.sampleCount++;
    if (isExtrapolated) bucket.extrapolated++;
    if (isHitched) bucket.hitched++;
    bucket.depthSum += bufferDepth;
  }

  rollup(type: SnapshotEntityType, nowMs: number): SnapshotStatsPerTypeWire {
    const sec = Math.floor(nowMs / 1000);
    const cutoff = sec - WINDOW_SEC;
    const inWindow = this.buckets.filter((b) => b.sec >= cutoff);
    let pushCount = 0, gapSum = 0, sampleCount = 0, extrapolated = 0, hitched = 0, depthSum = 0;
    const gapBuckets = GAP_BUCKETS.map(() => 0);
    for (const b of inWindow) {
      pushCount += b.pushCount;
      gapSum += b.gapSum;
      sampleCount += b.sampleCount;
      extrapolated += b.extrapolated;
      hitched += b.hitched;
      depthSum += b.depthSum;
      for (let i = 0; i < gapBuckets.length; i++) gapBuckets[i] += b.gapBuckets[i];
    }
    const meanGapMs = pushCount > 0 ? gapSum / pushCount : 0;
    const histogram: SnapshotGapBucketWire[] = GAP_BUCKETS.map((b, i) => ({
      bucket: b.label,
      count: gapBuckets[i],
      threshold: Number.isFinite(b.hi) ? b.hi : null,
    }));
    return {
      type,
      pushCount,
      sampleCount,
      meanGapMs,
      effectiveHz: meanGapMs > 0 ? 1000 / meanGapMs : 0,
      extrapolatedFraction: sampleCount > 0 ? extrapolated / sampleCount : 0,
      hitchedFraction: sampleCount > 0 ? hitched / sampleCount : 0,
      meanBufferDepth: sampleCount > 0 ? depthSum / sampleCount : 0,
      histogram,
    };
  }
}

class SnapshotStatsTrackerImpl {
  enabled = false;
  private channels: Record<SnapshotEntityType, TypeChannel> = {
    player: new TypeChannel(),
    mob: new TypeChannel(),
  };

  constructor() {
    browserOnly(() => {
      try {
        if (bootSearchParams().get(STORAGE_FLAG_NAME) === '1') {
          this.enabled = true;
        }
      } catch { /* ignore */ }
    });
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.clear();
  }

  /** Producer hook — call at the interpolator push() site. `now` is the
   *  same Date.now() passed to interp.push(). */
  recordPush(type: SnapshotEntityType, entityId: string, now: number): void {
    if (!this.enabled) return;
    this.channels[type].recordPush(entityId, now);
  }

  /** Producer hook — call after interp.sample(). Pass the SampleResult flags
   *  and the live buffer depth (interp.getSnapshotCount()). */
  recordSample(
    type: SnapshotEntityType,
    now: number,
    isExtrapolated: boolean,
    isHitched: boolean,
    bufferDepth: number,
  ): void {
    if (!this.enabled) return;
    this.channels[type].recordSample(now, isExtrapolated, isHitched, bufferDepth);
  }

  getSnapshot(): SnapshotStatsSnapshotWire {
    const now = Date.now();
    return {
      capturedAt: now,
      enabled: this.enabled,
      windowMs: WINDOW_SEC * 1000,
      perType: [
        this.channels.player.rollup('player', now),
        this.channels.mob.rollup('mob', now),
      ],
    };
  }

  clear(): void {
    this.channels.player = new TypeChannel();
    this.channels.mob = new TypeChannel();
  }
}

export const SnapshotStatsTracker = new SnapshotStatsTrackerImpl();

if (typeof window !== 'undefined') {
  (window as any).__SNAPSHOT_STATS_TRACKER__ = SnapshotStatsTracker;
}
